import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Package2, 
  ArrowLeft, 
  Loader2, 
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { inventoryReportApiService, MaterialIssueReportDto } from '../services/inventoryReportApi';
import { useBranch } from '../contexts/BranchContext';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { getMaterialIssueReportConfig } from '../config/uiConfig';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const MaterialIssueReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<MaterialIssueReportDto[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedItemName, setSelectedItemName] = useState<string>('');
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [defaultCategorySet, setDefaultCategorySet] = useState(false);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [selectedDateLabel, setSelectedDateLabel] = useState<string | null>(null);
  
  // Get config
  const materialIssueConfig = getMaterialIssueReportConfig();
  
  // Date range picker - default to current month
  const now = new Date();
  const getCurrentMonthStart = () => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };
  
  const getCurrentMonthEnd = () => {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  };

  const [startDate, setStartDate] = useState<string>(getCurrentMonthStart());
  const [endDate, setEndDate] = useState<string>(getCurrentMonthEnd());

  const loadReport = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch to view the report');
      return;
    }

    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    try {
      setLoading(true);
      
      const response = await inventoryReportApiService.getMaterialIssueReportByBranch(
        selectedBranch,
        startDate,
        endDate
      );
      
      if (response.success && response.data) {
        setReportData(response.data);
        toast.success('Report loaded successfully');
      } else {
        toast.error(response.message || 'Failed to load report');
        setReportData([]);
      }
    } catch (error) {
      // console.error('Error loading report:', error);
      toast.error('Failed to load report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load categories and set default category from config
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await itemCategoryApiService.getItemCategories();
        
        // Filter categories to only show allowed categories from config
        const allowedCategoryNames = materialIssueConfig.allowedCategories.map(cat => cat.toLowerCase().trim());
        const filteredCategories = categoriesData.filter((cat) => {
          const categoryName = cat.categoryName?.trim().toLowerCase() || '';
          return allowedCategoryNames.includes(categoryName);
        });
        
        setCategories(filteredCategories || []);
        
        // Set default category from config after categories are loaded (only if not already set)
        if (!defaultCategorySet && filteredCategories && filteredCategories.length > 0 && materialIssueConfig.defaultCategory) {
          const defaultCategoryName = materialIssueConfig.defaultCategory.trim();
          const defaultCategory = filteredCategories.find(
            (cat) => cat.categoryName?.trim().toLowerCase() === defaultCategoryName.toLowerCase()
          );
          if (defaultCategory && defaultCategory.id) {
            setSelectedCategoryId(defaultCategory.id.toString());
            setDefaultCategorySet(true);
            // console.log(`Default category set to: ${defaultCategory.categoryName} (ID: ${defaultCategory.id})`);
          } else {
            console.warn(`Default category "${materialIssueConfig.defaultCategory}" not found in allowed categories. Available:`, 
              filteredCategories.map(c => c.categoryName).join(', '));
          }
        }
      } catch (error) {
        // console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (selectedBranch !== null) {
      loadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, startDate, endDate]); // Reload when branch or date range changes

  // Get available item names filtered by selected category
  const availableItemNames = useMemo(() => {
    let items = reportData;
    
    // First filter by category if one is selected
    if (selectedCategoryId) {
      items = items.filter((item) => 
        item.rawCategoryId?.toString() === selectedCategoryId
      );
    }
    
    // Get unique item names
    const uniqueItemNames = new Set<string>();
    items.forEach((item) => {
      if (item.rawItemName) {
        uniqueItemNames.add(item.rawItemName);
      }
    });
    
    return Array.from(uniqueItemNames).sort();
  }, [reportData, selectedCategoryId]);

  // Filter data by category and item name
  const filteredData = useMemo(() => {
    let filtered = reportData;
    
    // Filter by category
    if (selectedCategoryId) {
      filtered = filtered.filter((item) => 
        item.rawCategoryId?.toString() === selectedCategoryId
      );
    }
    
    // Filter by item name (exact match)
    if (selectedItemName) {
      filtered = filtered.filter((item) => 
        item.rawItemName === selectedItemName
      );
    }
    
    return filtered;
  }, [reportData, selectedCategoryId, selectedItemName]);

  // Helper to build a YYYY-MM-DD key from a Date
  const toDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Process data for chart - group by day for selected date range
  const chartData = useMemo(() => {
    if (!startDate || !endDate) return [];
    
    // Parse start and end dates
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate number of days in range
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const maxDays = Math.min(diffDays, 90); // Cap at 90 days for performance
    
    // Initialize array for all days in range with 0 values
    const showMonth = diffDays > 31 || start.getMonth() !== end.getMonth();
    const daysData = Array.from({ length: maxDays }, (_, i) => {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const day = currentDate.getDate();
      // Show month/day if range spans multiple months, otherwise just day
      const dateLabel = showMonth 
        ? currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : day.toString();
      const dateKey = toDateKey(currentDate);
      return {
        day: day,
        date: dateLabel,
        fullDate: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        quantity: 0,
        dateObj: currentDate,
        dateKey,
      };
    });

    // Process filtered report data and aggregate by day using issueQuantity
    filteredData.forEach((item) => {
      if (item.issueDate && item.issueQuantity) {
        try {
          // Parse date string like "2025-04-11 05:30:00"
          // Handle both "YYYY-MM-DD HH:mm:ss" and ISO formats
          let issueDate: Date;
          if (item.issueDate.includes(' ')) {
            // Format: "2025-04-11 05:30:00"
            const [datePart, timePart] = item.issueDate.split(' ');
            const [year, month, day] = datePart.split('-').map(Number);
            issueDate = new Date(year, month - 1, day); // month is 0-indexed
          } else {
            // ISO format or other
            issueDate = new Date(item.issueDate);
          }
          
          // Check if date is valid
          if (isNaN(issueDate.getTime())) {
            // console.warn('Invalid date:', item.issueDate);
            return;
          }
          
          // Only process if date is within selected date range
          const issueDateOnly = new Date(issueDate.getFullYear(), issueDate.getMonth(), issueDate.getDate());
          const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
          
          if (issueDateOnly >= startDateOnly && issueDateOnly <= endDateOnly) {
            // Find the index of the day in the daysData array by comparing dates
            const dayIndex = daysData.findIndex(d => {
              if (!d.dateObj) return false;
              const dDateOnly = new Date(d.dateObj.getFullYear(), d.dateObj.getMonth(), d.dateObj.getDate());
              return dDateOnly.getTime() === issueDateOnly.getTime();
            });
            
            if (dayIndex >= 0 && dayIndex < maxDays) {
              const quantity = Number(item.issueQuantity) || 0;
              daysData[dayIndex].quantity += quantity;
            }
          }
        } catch (error) {
          // console.error('Error parsing issue date:', item.issueDate, error);
        }
      }
    });

    return daysData;
  }, [filteredData, startDate, endDate]);

  // X-axis label step to avoid overlapping labels when there are many days
  const xAxisLabelStep = useMemo(() => {
    const length = chartData.length || 0;
    if (length <= 15) return 1; // show all labels for small ranges
    // Aim for at most ~12 labels on the x-axis
    return Math.ceil(length / 12);
  }, [chartData]);

  // Items for the currently selected chart date (from bar click)
  const itemsForSelectedDate = useMemo(() => {
    if (!selectedDateKey) return [];

    return filteredData.filter((item) => {
      if (!item.issueDate) return false;

      try {
        let issueDate: Date;
        if (item.issueDate.includes(' ')) {
          const [datePart] = item.issueDate.split(' ');
          const [year, month, day] = datePart.split('-').map(Number);
          issueDate = new Date(year, month - 1, day);
        } else {
          issueDate = new Date(item.issueDate);
        }

        if (isNaN(issueDate.getTime())) {
          return false;
        }

        const issueDateOnly = new Date(
          issueDate.getFullYear(),
          issueDate.getMonth(),
          issueDate.getDate()
        );

        return toDateKey(issueDateOnly) === selectedDateKey;
      } catch {
        return false;
      }
    });
  }, [filteredData, selectedDateKey]);

  // Calculate top 10 items by issue quantity
  const topItems = useMemo(() => {
    // Aggregate by item name
    const itemMap = new Map<string, { name: string; quantity: number; sku?: string }>();
    
    filteredData.forEach((item) => {
      if (item.rawItemName && item.issueQuantity) {
        const itemName = item.rawItemName;
        const quantity = Number(item.issueQuantity) || 0;
        
        if (itemMap.has(itemName)) {
          const existing = itemMap.get(itemName)!;
          existing.quantity += quantity;
        } else {
          itemMap.set(itemName, {
            name: itemName,
            quantity: quantity,
            sku: item.rawItemSku
          });
        }
      }
    });
    
    // Convert to array, sort by quantity (descending), and take top 10
    return Array.from(itemMap.values())
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);
  }, [filteredData]);

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Package2 className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Material Issue Report</CardTitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadReport}
                  disabled={loading || !selectedBranch}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Report Content */}
        {!selectedBranch ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Package2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">Please select a branch to view the report</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filters - always visible when a branch is selected */}
            <Card className="border-primary-200 bg-white">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        Filter by Category:
                      </label>
                      <Select 
                        value={selectedCategoryId || "all"} 
                        onValueChange={(value) => {
                          setSelectedCategoryId(value === "all" ? "" : value);
                          // Reset item name filter when category changes
                          setSelectedItemName("");
                        }}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {loadingCategories ? (
                            <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                          ) : categories.length === 0 ? (
                            <SelectItem value="no-categories" disabled>No categories found</SelectItem>
                          ) : (
                            categories
                              .filter(cat => cat.isActive)
                              .map((category) => (
                                <SelectItem key={category.id} value={category.id.toString()}>
                                  {category.categoryName}
                                </SelectItem>
                              ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-4 flex-1">
                      <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                        Date Range:
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 whitespace-nowrap">From:</label>
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-40"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 whitespace-nowrap">To:</label>
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-40"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Filter by Item Name:
                    </label>
                    <Select 
                      value={selectedItemName || "all"} 
                      onValueChange={(value) => setSelectedItemName(value === "all" ? "" : value)}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Select item name" />
                      </SelectTrigger>
                      <SelectContentWithSearch searchPlaceholder="Search item name...">
                        <SelectItem value="all">All Items</SelectItem>
                        {availableItemNames.length === 0 ? (
                          <SelectItem value="no-items" disabled>No items found</SelectItem>
                        ) : (
                          availableItemNames.map((itemName) => (
                            <SelectItem key={itemName} value={itemName}>
                              {itemName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContentWithSearch>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Main content: show loader / empty state / chart + tables */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mr-3" />
                <span className="text-gray-600">Loading report data...</span>
              </div>
            ) : reportData.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Package2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No data available for this report</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try changing the date range, selecting a different category, or check back later
                  </p>
                </div>
              </div>
            ) : (
            <div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Chart - Takes 2 columns */}
              <Card className="border-primary-200 bg-white shadow-lg lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-gray-800">
                    Material Issue by Date
                    {startDate && endDate && (
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        - {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                    {selectedCategoryId && (
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        - {categories.find(c => c.id?.toString() === selectedCategoryId)?.categoryName}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="w-full">
                    <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        axisLine={{ stroke: '#e0e0e0' }}
                        tickLine={{ stroke: '#e0e0e0' }}
                        interval={0}
                        minTickGap={0}
                        tickFormatter={(value, index) => (index % xAxisLabelStep === 0 ? value : '')}
                      />
                      <YAxis 
                        label={{ value: 'Quantity Issued', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                        axisLine={{ stroke: '#e0e0e0' }}
                        tickLine={{ stroke: '#e0e0e0' }}
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <Tooltip 
                        formatter={(value: number) => [formatNumber(value), 'Quantity Issued']}
                        labelFormatter={(label, payload) => {
                          if (payload && payload[0] && payload[0].payload) {
                            return payload[0].payload.fullDate;
                          }
                          return label;
                        }}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px'
                        }}
                      />
                      <Bar 
                        dataKey="quantity" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              selectedDateKey === entry.dateKey
                                ? "#1e40af" // darker blue for selected
                                : "#3b82f6" // blue-500
                            }
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (entry.dateKey) {
                                setSelectedDateKey(entry.dateKey);
                                setSelectedDateLabel(entry.fullDate || entry.date || '');
                              }
                            }}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Summary Stats - Inside the chart card */}
                {(() => {
                  // Calculate average quantity per day (excluding future dates)
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  
                  // Filter chart data to only include days up to today and within date range
                  const validDaysData = chartData.filter((dayData) => {
                    if (!dayData.dateObj) return false;
                    const dayDate = new Date(dayData.dateObj);
                    dayDate.setHours(0, 0, 0, 0);
                    return dayDate <= today && dayData.quantity > 0;
                  });
                  
                  // Calculate total quantity and number of days with data
                  const totalQuantity = validDaysData.reduce((sum, day) => sum + day.quantity, 0);
                  const daysWithData = validDaysData.length;
                  const averageQuantity = daysWithData > 0 ? totalQuantity / daysWithData : 0;
                  
                  // Total quantity from all filtered data
                  const totalQuantityAll = filteredData.reduce((sum, item) => sum + (Number(item.issueQuantity) || 0), 0);
                  
                  return (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="flex items-center justify-center gap-8">
                        <div className="text-center">
                          <div className="text-sm text-gray-600">Average Quantity per Day</div>
                          <div className="text-2xl font-bold text-primary-600">
                            {formatNumber(averageQuantity)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-gray-600">Total Quantity Issued</div>
                          <div className="text-2xl font-bold text-blue-600">
                            {formatNumber(totalQuantityAll)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Top 10 Items Table - Takes 1 column */}
            <Card className="border-primary-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Top 10 Items Issued
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topItems.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold w-8">#</TableHead>
                          <TableHead className="font-semibold">Item Name</TableHead>
                          <TableHead className="font-semibold text-right">Quantity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topItems.map((item, index) => (
                          <TableRow key={index} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-gray-600">
                              {index + 1}
                            </TableCell>
                            <TableCell>
                              <div className="max-w-[200px]">
                                <div className="font-medium text-sm truncate" title={item.name}>
                                  {item.name}
                                </div>
                                {item.sku && (
                                  <div className="text-xs text-gray-500">SKU: {item.sku}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-semibold text-primary-600">
                              {formatNumber(item.quantity)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No items found
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Items for selected date (from chart click) */}
          {selectedDateKey && (
            <Card className="border-primary-200 bg-white shadow-lg mt-4">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Material Issues on {selectedDateLabel || selectedDateKey}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {itemsForSelectedDate.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Heat Number</TableHead>
                          <TableHead className="font-semibold">Work Order</TableHead>
                          <TableHead className="font-semibold">Endgood Name</TableHead>
                          <TableHead className="font-semibold">Item Name</TableHead>
                          <TableHead className="font-semibold text-right">Issue Qty</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {itemsForSelectedDate.map((item, index) => (
                          <TableRow key={item.id ?? index} className="hover:bg-gray-50">
                            <TableCell>{item.heatNumber || '-'}</TableCell>
                            <TableCell>
                              {item.workOrderId ? (
                                <Link
                                  to={`/work-orders/${item.workOrderId}/view`}
                                  className="text-primary-600 hover:underline"
                                >
                                  {item.workOrderNumber || `WO-${item.workOrderId}`}
                                </Link>
                              ) : (
                                item.workOrderNumber || '-'
                              )}
                            </TableCell>
                            <TableCell>{item.workOrderEndgoodName || item.endgoodName || '-'}</TableCell>
                            <TableCell>{item.rawItemName || '-'}</TableCell>
                            <TableCell className="text-right font-semibold text-primary-600">
                              {formatNumber(item.issueQuantity || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No material issues found for this date
                  </div>
                )}
              </CardContent>
            </Card>
          )}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialIssueReportPage;

